import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Briefcase, Clock, CheckCircle2, AlertTriangle, MapPin, Users, UserCheck, Zap, Sparkles, Search as SearchIcon, FileSearch, FileText, PackageCheck } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { OverviewMap } from "@/components/OverviewMap";
import { AlertsPanel } from "@/components/AlertsPanel";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function Dashboard() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/metrics");
      setM(data);
    } catch (e) { toast.error("Failed to load metrics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role !== "server") load(); }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  if (user?.role === "server") return <Navigate to="/today" replace />;

  const seed = async () => {
    try {
      await api.post("/seed/demo");
      toast.success("Demo data seeded");
      load();
    } catch { toast.error("Seed failed"); }
  };

  return (
    <>
      <PageHeader
        eyebrow={`Signed in as ${user?.role}`}
        title="Overview"
        description="Live pulse across your service operations — pending, in the field, and cleared."
        actions={
          <>
            {user?.role === "admin" ? (
              <Button data-testid="seed-demo-button" variant="outline" onClick={seed}>
                <Sparkles className="h-4 w-4 mr-2" /> Seed demo data
              </Button>
            ) : null}
            <Link to="/jobs/new">
              <Button data-testid="dashboard-new-job-button" className="sm-green">
                <Briefcase className="h-4 w-4 mr-2" /> New job
              </Button>
            </Link>
          </>
        }
      />

      <div className="p-4 sm:p-8 space-y-6">
        <div className="rounded-2xl bg-[#0b1f3a] text-white p-5 sm:p-6 relative overflow-hidden" data-testid="metrics-grid">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#1f5fa8]/30 blur-2xl pointer-events-none" />
          <div className="absolute right-24 -bottom-20 h-48 w-48 rounded-full bg-[#e8b04b]/20 blur-2xl pointer-events-none" />
          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 divide-x-0 md:divide-x divide-white/10">
            <Metric icon={Briefcase} label="Active jobs" value={loading ? "—" : m?.active ?? 0} tone="white" to="/jobs" />
            <Metric icon={Clock} label="Pending service" value={loading ? "—" : m?.pending ?? 0} tone="amber" to="/jobs?status=open" />
            <Metric icon={CheckCircle2} label="Served" value={loading ? "—" : m?.served ?? 0} tone="emerald" to="/jobs?status=served" />
            <Metric icon={AlertTriangle} label="Rush / same-day" value={loading ? "—" : m?.rush ?? 0} tone="rose" to="/jobs?priority=rush" />
          </div>
          {((m?.unpaid_invoices ?? 0) > 0 || (m?.needs_invoice ?? 0) > 0 || user?.features?.asset_search || user?.features?.record_retrieval) && (
            <div className="relative mt-5 pt-4 border-t border-white/10 flex flex-wrap gap-x-7 gap-y-2 text-sm" data-testid="billing-metrics-grid">
              {(m?.needs_invoice ?? 0) > 0 && <Link to="/jobs?status=needs_invoice" data-testid="metric-needs-invoice" className="inline-flex items-center gap-2 hover:text-[#e8b04b]"><FileText className="h-4 w-4 text-rose-300" /><b>{m.needs_invoice}</b> finished, not yet invoiced</Link>}
              {(m?.unpaid_invoices ?? 0) > 0 && <Link to="/jobs?unpaid=1" data-testid="metric-unpaid-invoices" className="inline-flex items-center gap-2 hover:text-[#e8b04b]"><Clock className="h-4 w-4 text-amber-300" /><b>{m.unpaid_invoices}</b> unpaid invoices</Link>}
              {user?.features?.asset_search && <Link to="/jobs?kind=asset_search" data-testid="metric-open-asset" className="inline-flex items-center gap-2 hover:text-[#e8b04b]"><SearchIcon className="h-4 w-4 text-fuchsia-300" /><b>{loading ? "—" : m?.open_asset ?? 0}</b> open asset searches</Link>}
              {user?.features?.record_retrieval && <Link to="/jobs?kind=record_retrieval" data-testid="metric-open-records" className="inline-flex items-center gap-2 hover:text-[#e8b04b]"><FileSearch className="h-4 w-4 text-teal-300" /><b>{loading ? "—" : m?.open_records ?? 0}</b> open record pulls</Link>}
              {user?.features?.document_courier && <Link to="/jobs?kind=document_courier" data-testid="metric-open-courier" className="inline-flex items-center gap-2 hover:text-[#e8b04b]"><PackageCheck className="h-4 w-4 text-orange-300" /><b>{loading ? "—" : m?.open_courier ?? 0}</b> open drop-offs / pick-ups</Link>}
              {(user?.features?.asset_search || user?.features?.record_retrieval) && <Link to="/jobs?status=completed" data-testid="metric-completed-kinds" className="inline-flex items-center gap-2 hover:text-[#e8b04b]"><CheckCircle2 className="h-4 w-4 text-emerald-300" /><b>{loading ? "—" : m?.completed_kinds ?? 0}</b> reports delivered</Link>}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <OverviewMap isAdmin={user?.role === "admin"} />
            <Card className="border-slate-200 shadow-sm">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-display font-bold tracking-tight">Recent service attempts</h3>
                  <p className="text-xs text-slate-500 mt-0.5">GPS-verified field log</p>
                </div>
                <Link to="/attempts" className="text-xs font-mono uppercase tracking-widest text-blue-600 hover:text-blue-700">View all →</Link>
              </div>
              <div className="divide-y divide-slate-100">
                {(m?.recent_attempts || []).length === 0 ? (
                  <EmptyRow label="No attempts logged yet." />
                ) : (
                  (m?.recent_attempts || []).map((a) => (
                    <div key={a.id} className="px-5 py-3 flex items-start gap-4 hover:bg-slate-50">
                      <div className="mt-0.5">
                        <MapPin className={`h-4 w-4 ${a.outcome === "served" ? "text-emerald-600" : "text-amber-600"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-slate-500">#{a.job_number}</span>
                          <span className="text-sm font-semibold text-slate-900 truncate">{a.recipient_name}</span>
                          <OutcomeBadge outcome={a.outcome} />
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{a.address}</div>
                        {a.notes ? <div className="text-xs text-slate-600 mt-1 italic">&ldquo;{a.notes}&rdquo;</div> : null}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-mono text-slate-500">{fmt(a.attempted_at)}</div>
                        {a.gps_lat ? (
                          <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                            {a.gps_lat.toFixed(4)}, {a.gps_lng.toFixed(4)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <AlertsPanel />
            <Card className="border-slate-200 shadow-sm">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                <Zap className="h-4 w-4 text-rose-600" />
                <h3 className="text-sm font-display font-bold tracking-tight">Rush jobs</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {(m?.rush_jobs || []).length === 0 ? (
                  <EmptyRow label="No rush jobs. Nice." />
                ) : (
                  (m?.rush_jobs || []).map((j) => (
                    <Link key={j.id} to={`/jobs/${j.id}`} className="block px-5 py-3 hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-slate-500">#{j.job_number}</span>
                        <PriorityBadge priority={j.priority} />
                      </div>
                      <div className="text-sm font-semibold text-slate-900 mt-0.5 truncate">{j.recipient_name}</div>
                      <div className="text-xs text-slate-500 truncate">{j.service_type}</div>
                    </Link>
                  ))
                )}
              </div>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <MiniStat icon={Users} label="Clients" value={m?.clients ?? 0} />
              <MiniStat icon={UserCheck} label="Servers" value={m?.servers ?? 0} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Metric({ icon: Icon, label, value, tone, to }) {
  const tones = { white: "text-white bg-white/10", amber: "text-amber-300 bg-amber-400/15", emerald: "text-emerald-300 bg-emerald-400/15", rose: "text-rose-300 bg-rose-400/15" };
  return (
    <Link to={to || "/jobs"} className="group flex items-center gap-3 md:pl-6 first:pl-0" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-300 truncate">{label}</div>
        <div className="text-3xl font-display font-bold leading-none mt-1 tabular-nums">{value}</div>
      </div>
    </Link>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <Card className="border-slate-200 p-4">
      <Icon className="h-4 w-4 text-slate-400" />
      <div className="text-xl font-display font-bold mt-2">{value}</div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
    </Card>
  );
}

function EmptyRow({ label, hint, to }) {
  return (
    <div className="px-5 py-10 text-center">
      <div className="mx-auto h-9 w-9 rounded-full bg-slate-100 grid place-items-center text-slate-400 mb-2"><Sparkles className="h-4 w-4" /></div>
      <div className="text-sm text-slate-500">{label}</div>
      {hint && to && <Link to={to} className="sm-link text-xs mt-1 inline-block">{hint} →</Link>}
    </div>
  );
}

function OutcomeBadge({ outcome }) {
  const map = {
    served: "bg-emerald-100 text-emerald-800 border-emerald-200",
    not_home: "bg-amber-100 text-amber-800 border-amber-200",
    bad_address: "bg-rose-100 text-rose-800 border-rose-200",
    refused: "bg-rose-100 text-rose-800 border-rose-200",
    moved: "bg-slate-100 text-slate-700 border-slate-300",
    other: "bg-slate-100 text-slate-700 border-slate-300",
  };
  return <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[outcome] || map.other}`}>{outcome?.replace("_", " ")}</span>;
}

